<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import axios from '../axios'

const props = defineProps({
  playerId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['games-loaded'])

const games = ref([])
const loading = ref(false)
const error = ref(null)

// Computed properties for stats
const totalGames = computed(() => games.value.length)
const avgPoints = computed(() => {
  if (games.value.length === 0) return 0
  const total = games.value.reduce((sum, game) => sum + (game.points || 0), 0)
  return (total / games.value.length).toFixed(1)
})
const avgRebounds = computed(() => {
  if (games.value.length === 0) return 0
  const total = games.value.reduce((sum, game) => sum + (game.total_rebounds || 0), 0)
  return (total / games.value.length).toFixed(1)
})
const avgAssists = computed(() => {
  if (games.value.length === 0) return 0
  const total = games.value.reduce((sum, game) => sum + (game.assists || 0), 0)
  return (total / games.value.length).toFixed(1)
})

const fetchGames = async () => {
  loading.value = true
  error.value = null
  
  try {
    const response = await axios.get(`/api/v2/games/player/${props.playerId}`)
    games.value = response.data || []
  } catch (err) {
    console.error('Error fetching player games:', err)
    error.value = 'Failed to load player games'
  } finally {
    loading.value = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatMinutes = (minutes) => {
  if (!minutes) return '-'
  return `${minutes} min`
}

const formatPercentage = (value) => {
  if (!value) return '-'
  return `${value}%`
}

// Watch for games data changes and emit to parent
watch(games, (newGames) => {
  if (newGames && newGames.length > 0) {
    emit('games-loaded', newGames)
  }
}, { immediate: true })

onMounted(() => {
  if (props.playerId) {
    fetchGames()
  }
})
</script>

<template>
  <div class="space-y-6">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
      <div class="text-gray-500 mt-2">Loading games...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-12">
      <div class="text-red-500 text-lg">{{ error }}</div>
      <button 
        @click="fetchGames" 
        class="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
      >
        Retry
      </button>
    </div>

    <!-- Games Data -->
    <div v-else-if="games.length > 0" class="space-y-6">
      <!-- Summary Stats -->
      <div class="bg-white rounded-lg border border-gray-200 p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Season Summary</h3>
        <div class="grid grid-cols-4 gap-4">
          <div class="text-center">
            <div class="text-2xl font-bold text-blue-600">{{ totalGames }}</div>
            <div class="text-sm text-gray-500">Games Played</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-blue-600">{{ avgPoints }}</div>
            <div class="text-sm text-gray-500">Avg Points</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-blue-600">{{ avgRebounds }}</div>
            <div class="text-sm text-gray-500">Avg Rebounds</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-blue-600">{{ avgAssists }}</div>
            <div class="text-sm text-gray-500">Avg Assists</div>
          </div>
        </div>
      </div>

      <!-- Games Table -->
      <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900">Game History</h3>
        </div>
        
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>

                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">League</th>
                <!-- INSERT_YOUR_CODE -->
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Home</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opponent</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PTS</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REB</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AST</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">STL</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BLK</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="game in games" :key="game.id" class="hover:bg-gray-50">
                <!-- Date -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDate(game.game_date) }}
                </td>
                <!-- League -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ game.league_name || game.league || '-' }}
                </td>
                <!-- Team -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ game.team_name || game.team || '-' }}
                </td>
                <!-- Home/Away -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span v-if="game.is_home !== undefined">
                    {{ game.team ? 'Home' : 'Away' }}
                  </span>
                  <span v-else>-</span>
                </td>
                <!-- Opponent -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ game.opponent_team_name || game.opponent_team || '-' }}
                </td>
                <!-- Minutes -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatMinutes(game.minutes) }}
                </td>
                <!-- Points -->
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ game.points || 0 }}
                </td>
                <!-- Rebounds -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ game.total_rebounds || 0 }}
                </td>
                <!-- Assists -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ game.assists || 0 }}
                </td>
                <!-- Steals -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ game.steals || 0 }}
                </td>
                <!-- Blocks -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ game.blocks || 0 }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- No Games State -->
    <div v-else class="text-center py-12">
      <div class="text-gray-500 text-lg">No games found for this player</div>
    </div>
  </div>
</template>
